import {
  ActionIcon,
  Anchor,
  Box,
  Button,
  Card,
  Code,
  CopyButton,
  Grid,
  Group,
  Notification,
  Stack,
  Text,
  Title,
  Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconCheck,
  IconCopy,
  IconHistory,
  IconKey,
  IconPlus,
  IconShieldHalf,
  IconWallet,
} from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { type Balance, formatCurrency, LOGIN_METHOD } from '@/api';
import { ConnectedAccounts } from '@/components/ConnectedAccounts/ConnectedAccounts';
import {
  OidcSyncFromIdpButton,
  OidcTwoFactorRedirect,
} from '@/components/Security/OidcHandoffHints';
import { VerifiedEmailDetail } from '@/components/VerifiedEmail/Detail';
import { useIdpHandoff } from '@/hooks/useIdpHandoff';
import { useLanguageAwareRouting } from '@/hooks/useLanguageAwareRouting';
import { useOidcRefreshOnReturn } from '@/hooks/useOidcRefreshOnReturn';
import { NewDepositForm } from '../../components/Billing/Deposit';
import { PageHeader } from '../../components/Common/PageHeader';
import { TwoFactorSettings } from '../../components/Security/TwoFactorSettings';
import { API_BASE_URL } from '../../constants';
import { useRustyState } from '../../state';

export interface AccountProfilePageProps {
  serviceDomain: string;
  serviceEmail: string;
}

const PROVIDERS_WITH_PUBLIC_KEY: ReadonlyArray<LOGIN_METHOD> = [
  LOGIN_METHOD.NOSTR,
  LOGIN_METHOD.MANUAL,
  LOGIN_METHOD.PRIVATE_KEY,
];

export function AccountProfilePage({ serviceEmail }: AccountProfilePageProps) {
  const { t } = useTranslation();
  const { createLanguageURL } = useLanguageAwareRouting();
  const { api } = useRustyState.getState();
  const session = useRustyState((state) => state.api?.auth?.getSession());
  const loginMethod = session?.method as LOGIN_METHOD | undefined;
  const publicKey = session?.publicKey;
  const [balance, setBalance] = useState<Balance[]>([]);
  const [addMoney, setAddMoney] = useState(false);
  const [depositStatus, setDepositStatus] = useState<'success' | 'cancel' | ''>('');
  const [syncing, setSyncing] = useState(false);

  // OIDC handoff config drives the IdP deep-links. `null` when this isn't an
  // OIDC-backed deployment (most users see the existing local UI unchanged).
  const apiRoot = API_BASE_URL.replace(/\/v1\/?$/, '');
  const isOidc = loginMethod === LOGIN_METHOD.OIDC;
  const { config: handoffConfig } = useIdpHandoff(apiRoot);

  // Auto-trigger a silent OIDC refresh when the user comes back via the
  // portal handoff banner (URL carries `?refreshed=1`). Strips the flag and
  // bounces through Hydra to repopulate the local profile from the IdP.
  useOidcRefreshOnReturn({ session, auth: api.auth });

  const handleOidcSync = async () => {
    if (!api.auth) return;
    setSyncing(true);
    try {
      const returnTo = `${window.location.origin}${window.location.pathname}${window.location.hash.split('?')[0]}?refreshed=1`;
      const authUrl = await api.auth.initiateOidcRefresh(returnTo);
      window.location.href = authUrl;
    } catch (e) {
      console.error('OIDC sync failed:', e);
      setSyncing(false);
    }
  };

  useEffect(() => {
    const url = new URL(window.location.href);
    const hashParams = new URLSearchParams(url.hash.split('?')[1]);
    const deposit = hashParams.get('deposit');

    if (deposit && (deposit === 'success' || deposit === 'cancel')) {
      setDepositStatus(deposit);
    }

    api
      .getAccountBalance()
      .then(setBalance)
      .catch(() => setBalance([]));
  }, []);

  const showPublicKey =
    !!loginMethod && PROVIDERS_WITH_PUBLIC_KEY.includes(loginMethod) && !!publicKey;

  return (
    <>
      {depositStatus === 'success' && (
        <Notification
          color="green"
          title={t('profile.depositSuccess')}
          mb="md"
          onClose={() => setDepositStatus('')}
        >
          {t('profile.depositSuccessMessage')}
        </Notification>
      )}

      <PageHeader
        title={t('glob_navigation.profile')}
        subtitle={t('profile.headerSubtitle', 'Account, credits, login, and privacy.')}
      />

      {isOidc && (
        <Group mb="md">
          <OidcSyncFromIdpButton onSync={handleOidcSync} busy={syncing} />
        </Group>
      )}

      <Grid gutter="md">
        {/* LEFT: Email Addresses */}
        <Grid.Col span={{ base: 12, lg: 7 }}>
          <Card withBorder shadow="xs" radius="md" p="lg">
            <Title order={4} mb="xs">
              {t('profile.emails')}
            </Title>
            <Text size="sm" c="dimmed" mb="md">
              {t('profile.emailsDescription')}
            </Text>
            <VerifiedEmailDetail
              submitFormCb={api.newVerifiedEmail}
              deleteCb={api.deleteVerifiedEmail}
              verifyCb={api.verifyVerifiedEmail}
              setAccountEmailCb={api.setAccountEmail}
              getVerifiedEmails={api.getVerifiedEmails}
            />
          </Card>
        </Grid.Col>

        {/* RIGHT */}
        <Grid.Col span={{ base: 12, lg: 5 }}>
          <Stack gap="md">
            {/* Available Funds */}
            <Card withBorder shadow="xs" radius="md" p="lg">
              <Group justify="space-between" align="center" mb="sm">
                <Title order={4}>{t('profile.availableFunds')}</Title>
                <IconWallet size={20} color="var(--mantine-color-dimmed)" />
              </Group>
              <Box mb="md">
                {balance && balance.length > 0 ? (
                  balance.map((bal, i) => (
                    <Text key={`balance-${i}`} size="xl" fw={700}>
                      {formatCurrency(bal)}
                    </Text>
                  ))
                ) : (
                  <Text size="sm" c="dimmed">
                    {t('profile.noBalanceFound')}
                  </Text>
                )}
              </Box>
              {addMoney ? (
                <>
                  <NewDepositForm onCancelCb={() => setAddMoney(false)} />
                  <Text size="sm" mt="xs">
                    {t('profile.wirePaymentInfo')}{' '}
                    <Anchor href={`mailto:${serviceEmail}`}>{serviceEmail}</Anchor>
                  </Text>
                </>
              ) : (
                <>
                  <Text size="sm" c="dimmed" mb="md">
                    {t('profile.lowBalance')}
                  </Text>
                  <Button
                    fullWidth
                    onClick={() => setAddMoney(true)}
                    leftSection={<IconPlus size={16} />}
                  >
                    {t('profile.addMoney')}
                  </Button>
                </>
              )}
            </Card>

            {/* Authentication Providers */}
            <Card withBorder shadow="xs" radius="md" p="lg">
              <Group justify="space-between" align="center" mb="xs">
                <Title order={4}>
                  {t('connectedAccounts.sectionTitle', 'Authentication Providers')}
                </Title>
                <IconKey size={20} color="var(--mantine-color-dimmed)" />
              </Group>
              <Text size="sm" c="dimmed" mb="md">
                {t(
                  'connectedAccounts.sectionDescription',
                  'These are the ways you can sign in to this account.'
                )}
              </Text>
              <ConnectedAccounts api={api} notifications={notifications} />
              {showPublicKey && (
                <>
                  <Text size="xs" c="dimmed" mt="md" mb="xs">
                    {t('profile.publicKeyHex')}
                  </Text>
                  <Group gap="xs" wrap="nowrap" align="center">
                    <Code
                      style={{
                        flex: 1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        padding: 'var(--mantine-spacing-xs) var(--mantine-spacing-sm)',
                      }}
                    >
                      {publicKey}
                    </Code>
                    <CopyButton value={publicKey ?? ''}>
                      {({ copied, copy }) => (
                        <Tooltip label={copied ? t('common.copied') : t('common.copy')}>
                          <ActionIcon variant="subtle" onClick={copy} aria-label={t('common.copy')}>
                            {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
                          </ActionIcon>
                        </Tooltip>
                      )}
                    </CopyButton>
                  </Group>
                </>
              )}
            </Card>

            {/* Security */}
            <Card withBorder shadow="xs" radius="md" p="lg">
              <Group justify="space-between" mb="md">
                <Title order={4}>{t('profile.security')}</Title>
                <IconShieldHalf size={20} color="var(--mantine-color-dimmed)" />
              </Group>
              <Stack gap="md">
                {isOidc ? (
                  <OidcTwoFactorRedirect
                    config={handoffConfig}
                    returnTo={`${window.location.origin}${window.location.pathname}${window.location.hash.split('?')[0]}?refreshed=1`}
                  />
                ) : (
                  <TwoFactorSettings compact />
                )}
                <Group
                  justify="space-between"
                  p="md"
                  style={{
                    border: '1px solid var(--mantine-color-default-border)',
                    borderRadius: 'var(--mantine-radius-md)',
                  }}
                >
                  <Group gap="md">
                    <Box
                      style={{
                        width: 40,
                        height: 40,
                        borderRadius: 999,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: 'var(--mantine-color-default-hover)',
                        color: 'var(--mantine-color-dimmed)',
                      }}
                    >
                      <IconHistory size={20} />
                    </Box>
                    <Box>
                      <Text fw={500} size="sm">
                        {t('profile.sessionHistory')}
                      </Text>
                    </Box>
                  </Group>
                  <Button
                    component={Link}
                    to={createLanguageURL('/account/security-log')}
                    variant="subtle"
                    size="sm"
                  >
                    {t('profile.viewLog')}
                  </Button>
                </Group>
              </Stack>
            </Card>

            {/* Data & Privacy */}
            <Card withBorder shadow="xs" radius="md" p="lg">
              <Title order={4} mb="xs">
                {t('profile.dataPrivacy')}
              </Title>
              <Text size="sm" c="dimmed" mb="md">
                {t('profile.dataPrivacyDescription')}
              </Text>
              <Button
                component={Link}
                to={createLanguageURL('/account/data-privacy')}
                variant="default"
                fullWidth
              >
                {t('profile.manageMyData')}
              </Button>
            </Card>
          </Stack>
        </Grid.Col>
      </Grid>
    </>
  );
}
